import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { ChangeDiff, SUPPLIER_LABELS, CUSTOMER_LABELS, BOM_LABELS } from "@/components/ChangeDiff";
import { MasterRejectDialog } from "@/components/MasterRejectDialog";
import { DT } from "@/components/ui/denseTable";
import { SortHeader, useSort } from "@/components/ui/SortHeader";
import { Inbox, ShoppingCart, Boxes, Users2, Contact, CheckCircle2, XCircle, Loader2, ExternalLink, RefreshCw } from "lucide-react";

const KIND_META = {
  po: { label: "Purchase Order", type: "PO", tab: "purchasing", Icon: ShoppingCart, chip: "bg-blue-100 text-blue-700", text: "text-blue-700" },
  bom: { label: "BOM Item", type: "BOM", tab: "bom", Icon: Boxes, chip: "bg-indigo-100 text-indigo-700", text: "text-indigo-700" },
  bom_refresh: { label: "BOM Refresh", type: "BOM Refresh", tab: "schedule", Icon: RefreshCw, chip: "bg-yellow-100 text-yellow-700", text: "text-yellow-700" },
  supplier: { label: "Supplier", type: "Supplier", tab: "suppliers", Icon: Users2, chip: "bg-teal-100 text-teal-700", text: "text-teal-700" },
  customer: { label: "Customer", type: "Customer", tab: "customers", Icon: Contact, chip: "bg-amber-100 text-amber-700", text: "text-amber-700" },
};

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
// Whole days waiting since submission; hours drives the >48h red highlight.
const pendingHours = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 3600000 : 0);

// Admin-only single queue for every pending approval: POs, BOM items, suppliers, customers.
export default function ApprovalsInbox({ facility, orders = [], bomItems = [], suppliers = [], customers = [], poVersion = 0, onApprovePO, onRejectPO, onApproveBom, onRejectBom, onApproveSupplier, onRejectSupplier, onApproveCustomer, onRejectCustomer, onApplyBomRefresh, onNavigate }) {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [rejecting, setRejecting] = useState(null); // entry being rejected

  const loadPos = () => {
    setLoading(true);
    api.get(`/purchase-orders?facility=${encodeURIComponent(facility)}`)
      .then((d) => setPos((Array.isArray(d) ? d : d.items || []).filter((p) => p.status === "Pending Approval")))
      .catch((e) => { logger.error("Approvals inbox: PO load failed:", e); setPos([]); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadPos, [facility, poVersion]);

  const entries = useMemo(() => {
    const rows = [];
    pos.forEach((p) => rows.push({ kind: "po", id: p.id, title: p.po_number, subtitle: `${p.supplier || "—"} · ${(p.items || []).map((i) => `${i.name} ×${i.ordered_qty}`).join(", ")}`, by: p.created_by, at: p.created_at, sla: p.sla_breached, raw: p }));
    bomItems.filter((b) => b.approval_status === "Pending Approval").forEach((b) => rows.push({ kind: "bom", id: b.id, title: b.name, subtitle: `${b.sku} · ${b.item_type}`, by: b.updated_by || b.created_by, at: b.updated_at || b.created_at, changes: b.pending_changes, labels: BOM_LABELS, raw: b }));
    orders.filter((o) => o.bom_outdated).forEach((o) => rows.push({ kind: "bom_refresh", id: o.id, title: o.order_code, subtitle: `${o.product_name} — production stages revised`, at: o.bom_revised_at, raw: o }));
    suppliers.filter((s) => s.status === "Pending Approval").forEach((s) => rows.push({ kind: "supplier", id: s.id, title: s.name, subtitle: [s.code, s.phone].filter(Boolean).join(" · "), by: s.created_by, at: s.created_at, changes: s.pending_changes, labels: SUPPLIER_LABELS, raw: s }));
    customers.filter((c) => c.status === "Pending Approval").forEach((c) => rows.push({ kind: "customer", id: c.id, title: c.name, subtitle: [c.code, c.phone].filter(Boolean).join(" · "), by: c.created_by, at: c.created_at, changes: c.pending_changes, labels: CUSTOMER_LABELS, raw: c }));
    // Longest-waiting first: oldest submission (smallest timestamp) on top.
    return rows.sort((a, b) => new Date(a.at || Date.now()) - new Date(b.at || Date.now()));
  }, [pos, orders, bomItems, suppliers, customers]);

  const counts = useMemo(() => {
    const m = { all: entries.length };
    entries.forEach((e) => { m[e.kind] = (m[e.kind] || 0) + 1; });
    return m;
  }, [entries]);
  const { sort, toggle, apply } = useSort();
  const visible = apply(filter === "all" ? entries : entries.filter((e) => e.kind === filter), {
    type: (e) => KIND_META[e.kind].type, name: (e) => e.title, by: (e) => e.by || "", at: (e) => new Date(e.at || 0).getTime(), days: (e) => pendingHours(e.at),
  });

  const handlers = {
    po: { approve: (e) => onApprovePO(e.raw), reject: (e, note) => onRejectPO(e.raw, note) },
    bom: { approve: (e) => onApproveBom(e.raw), reject: (e, r) => onRejectBom(e.raw, r) },
    supplier: { approve: (e) => onApproveSupplier(e.raw), reject: (e, r) => onRejectSupplier(e.raw, r) },
    customer: { approve: (e) => onApproveCustomer(e.raw), reject: (e, r) => onRejectCustomer(e.raw, r) },
    bom_refresh: { approve: (e) => onApplyBomRefresh(e.raw) },
  };
  const doApprove = async (e) => { await handlers[e.kind].approve(e); if (e.kind === "po") loadPos(); };
  const doReject = async (reason) => { const e = rejecting; await handlers[e.kind].reject(e, reason); if (e.kind === "po") loadPos(); };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="approvals-inbox">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <Inbox className="h-5 w-5 text-purple-600" />
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">Approvals Inbox</h3>
          <p className="text-xs text-slate-400">Everything waiting for your sign-off — purchase orders, BOM items, suppliers &amp; customers — with field-level changes inline.</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5" data-testid="approvals-filters">
          {[["all", "All"], ...Object.entries(KIND_META).map(([k, m]) => [k, m.label + "s"])].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`approvals-filter-${k}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-purple-600 border-purple-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-700"}`}>
              {label} <span className={filter === k ? "text-purple-100" : "text-slate-400"}>({counts[k] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="flex items-center justify-center gap-2 py-12 text-slate-400 text-sm" data-testid="approvals-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
      ) : visible.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400 italic" data-testid="approvals-empty">
          {filter === "all" ? "Nothing awaiting approval — you're all caught up." : `No ${KIND_META[filter].type.toLowerCase()} requests awaiting approval.`}
        </p>
      ) : (
        <div className={DT.wrap} data-testid="approvals-table">
          <table className={`${DT.table} min-w-[900px]`}>
            <thead className={DT.thead}>
              <tr className={DT.headerRow}>
                <SortHeader label="Type" k="type" sort={sort} onSort={toggle} cls={DT.th} />
                <SortHeader label="Item Name" k="name" sort={sort} onSort={toggle} cls={`${DT.th} ${DT.stickyLeft}`} />
                <SortHeader label="Submitted By" k="by" sort={sort} onSort={toggle} cls={DT.th} />
                <SortHeader label="Date Requested" k="at" sort={sort} onSort={toggle} cls={DT.th} />
                <SortHeader label="Days Pending" k="days" sort={sort} onSort={toggle} align="right" cls={DT.thRight} />
                <th className={DT.th}>Approval Status</th>
                <th className={`${DT.thRight} ${DT.stickyRight}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => {
                const meta = KIND_META[e.kind];
                const Icon = meta.Icon;
                const hrs = pendingHours(e.at);
                const days = Math.floor(hrs / 24);
                const overdue = hrs > 48;
                return (
                  <tr key={`${e.kind}-${e.id}`} className={DT.row} data-testid={`approval-row-${e.kind}-${e.id}`}>
                    <td className={DT.td}>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${meta.text}`} data-testid={`approval-type-${e.kind}-${e.id}`}>
                        <Icon className="h-3.5 w-3.5" />{meta.type}
                      </span>
                    </td>
                    <td className={`${DT.td} ${DT.stickyLeft} min-w-[260px]`}>
                      <p className="text-xs font-semibold text-slate-800">
                        {e.title}
                        {e.sla && <span className="ml-2 text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`approval-sla-${e.id}`}>⏰ SLA &gt;24h</span>}
                      </p>
                      {e.subtitle && <p className="text-[11px] text-slate-400 truncate max-w-[340px]">{e.subtitle}</p>}
                      {e.raw?.rejection_reason && (
                        <p className="text-[10px] text-rose-600 italic mt-0.5" data-testid={`approval-rejection-${e.kind}-${e.id}`}>Rejected earlier: {e.raw.rejection_reason}</p>
                      )}
                      {e.changes && <div className="max-w-md mt-1"><ChangeDiff changes={e.changes} labels={e.labels} testidPrefix={`approval-${e.kind}-${e.id}`} /></div>}
                      {onNavigate && (
                        <button onClick={() => onNavigate(meta.tab)} className="mt-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-0.5" data-testid={`approval-open-${e.kind}-${e.id}`}>
                          <ExternalLink className="h-3 w-3" /> Open in {meta.label === "BOM Item" ? "BOM" : meta.label === "Purchase Order" ? "Purchasing" : meta.label + "s"}
                        </button>
                      )}
                    </td>
                    <td className={`${DT.td} text-slate-600`}>{e.by || "system"}</td>
                    <td className={`${DT.td} text-slate-500 whitespace-nowrap`}>{e.at ? fmtWhen(e.at) : "—"}</td>
                    <td className={DT.tdRight}>
                      <span className={`font-mono-data font-bold ${overdue ? "text-red-600" : "text-slate-600"}`} data-testid={`approval-days-${e.kind}-${e.id}`} title={overdue ? "Waiting more than 48 hours" : ""}>
                        {days === 0 ? "<1" : days}d
                      </span>
                    </td>
                    <td className={DT.td}>
                      <span className="inline-block text-[10px] font-mono-data px-2 py-0.5 rounded bg-purple-50 text-purple-700" data-testid={`approval-status-${e.kind}-${e.id}`}>Pending Approval</span>
                    </td>
                    <td className={`${DT.td} ${DT.stickyRight}`}>
                      <div className={DT.actionsCell}>
                        <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => doApprove(e)} data-testid={`inbox-approve-${e.kind}-${e.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {e.kind === "bom_refresh" ? "Apply" : "Approve"}
                        </Button>
                        {e.kind !== "bom_refresh" && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 border-rose-200" onClick={() => setRejecting(e)} data-testid={`inbox-reject-${e.kind}-${e.id}`}>
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rejecting && (
        <MasterRejectDialog
          title={`Reject ${rejecting.title}`}
          description="Send this back to the planner. A reason is required so they know what to fix."
          onClose={() => setRejecting(null)}
          onConfirm={doReject}
        />
      )}
    </div>
  );
}
